import React, { useState } from "react";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Check, Copy } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

/* Batch 17 · F-139: ONE generic reply whatever the server found (no account-exists hint, no e-mail flag); the reset link block
   shows only when a dev / local copy returned `dev_reset_url` (never on live). */
export default function ForgotPasswordDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setEmail(""); setResult(null); };

  const submit = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setResult(data);
    } catch (e) {
      toast.error(toErrMsg(e, "Failed to send reset link"));
    } finally { setBusy(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.dev_reset_url);
    toast.success("Reset link copied");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent data-testid="forgot-password-dialog">
        <DialogHeader>
          <DialogTitle>{result ? t("auth.forgotDoneTitle") : t("auth.forgotTitle")}</DialogTitle>
          {/* Batch 19 · A3 (F-143): after the request the reply shows ONCE (the box below); the Batch 17 reply rules are unchanged */}
          {!result && <DialogDescription>{t("auth.forgotIntro")}</DialogDescription>}
        </DialogHeader>

        {!result && (
          <div className="grid gap-3 py-2">
            <div className="grid gap-1">
              <Label>Email</Label>
              <Input type="email" value={email} placeholder="you@yourfirm.com"
                     onChange={(e) => setEmail(e.target.value)}
                     data-testid="forgot-email" autoComplete="email"
                     onKeyDown={(e) => e.key === "Enter" && submit()} />
            </div>
          </div>
        )}

        {result && result.dev_reset_url && (
          <div className="space-y-2 py-2" data-testid="forgot-dev-link">
            <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900" data-testid="forgot-generic">
              <Check className="h-4 w-4 flex-none" />
              <span>{t("auth.forgotGeneric")} {t("auth.forgotValid")}</span>
            </div>
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
              <div>{t("auth.forgotDevLink")}</div>
            </div>
            <div className="rounded-md border bg-slate-50 p-2 text-[11px] font-mono text-slate-700 break-all">
              {result.dev_reset_url}
            </div>
            <Button variant="outline" size="sm" onClick={copyLink} className="w-full"
                    data-testid="copy-reset-link">
              <Copy className="me-2 h-3 w-3" /> Copy link
            </Button>
          </div>
        )}

        {result && !result.dev_reset_url && (
          <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[13px] text-emerald-900" data-testid="forgot-generic">
            <Check className="h-4 w-4 flex-none" />
            <span>{t("auth.forgotGeneric")} {t("auth.forgotValid")}</span>
          </div>
        )}

        <DialogFooter>
          {!result && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="forgot-cancel">Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="forgot-submit"
                      className="bg-slate-900 hover:bg-slate-800">
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </>
          )}
          {result && (
            <Button onClick={() => onOpenChange(false)} data-testid="forgot-close"
                    className="bg-slate-900 hover:bg-slate-800">Close</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
